import { TenantConst } from '@/constants'
import { isLocalDashboardHost } from '@/utils/dashboard-host'
import { createAuth0Client } from '@auth0/auth0-spa-js'
import { markRaw, reactive } from 'vue'

let instance

export const getInstance = () => instance

/**
 *  Auth service instance initialization.
 *  Vue 3 port: the Vue 2 `new Vue({...})` service instance is now a reactive()
 *  object with the same data fields and methods. The old
 *  `mixins: [EventEmitter.prototype]` was dead (no .on/.emit callers) and is
 *  removed, which also drops the webpack 'events' polyfill need.
 */

export const useAuth0 = ({
  onRedirectCallback = () => window.history.replaceState({}, document.title, window.location.pathname),
  redirectUri = window.location.origin,
  ...pluginOptions
}) => {
  if (instance) return instance

  instance = reactive({
    auth0Client: null,
    accessToken: null,
    idToken: null,
    idTokenRaw: '',
    isAuthenticated: false,
    isLoading: true,
    tenants: [],
    uid: -1,
    user: {},
    localStorageKey: 'loggedIn',
    segmentTraitsKey: 'ajs_user_traits',
    segmentIdKey: 'ajs_user_id',
    error: null,
    appState: null,
    isReadyPromise: null, // Define the promise here
    readyResolve: null, // Will hold the resolve function
    loginWithRedirect(options) {
      return this.auth0Client.loginWithRedirect(options)
    },
    logout(customReturnTo = '') {
      localStorage.removeItem(this.localStorageKey)
      this.auth0Client.logout({
        logoutParams: {
          returnTo: `${window.location.origin}${customReturnTo}`,
        },
      })

      this.clearPII()
    },
    async getAccessTokenSilently() {
      return await this.auth0Client.getTokenSilently()
    },
    // Clear Personally Identifiable Information (PII) to reduce vulnerabilities.
    clearPII() {
      if (window.analytics) {
        window.analytics.reset()
      } else {
        // If segment analytics is not loaded or not ready for any reason, manually clear.
        localStorage.removeItem(this.segmentTraitsKey)
        localStorage.setItem(this.segmentIdKey, 'null') // set to string 'null' to match with Segment reset() behaviour. Also in actual testing removeItem doesn't work on `ajs_user_id` item.
        AppHelper.deleteCookie(this.segmentIdKey)
      }

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)

        // Clear all the fingerprints left by tus uploader which might contain sensitive step file details
        // Successful upload fingerprints are cleared automatically, however failed upload and existing fingerprints need to be manually cleared.
        if (key.startsWith('tus::')) {
          localStorage.removeItem(key)
        }
      }
    },
    isReady() {
      return this.isReadyPromise
    },
  })

  // Init function: body of the Vue 2 `created()` hook, fired immediately
  // (and not awaited) exactly like the async created hook was.
  const init = async () => {
    instance.isReadyPromise = new Promise((resolve) => {
      instance.readyResolve = resolve
    })

    // markRaw: keep the auth0 client out of the reactive proxy (Vue 3 proxies
    // can break foreign class internals); nothing observes it deeply, so
    // observable behavior is unchanged from Vue 2.
    // Local testing points a localhost origin at a remote Auth0 tenant. The
    // default in-memory token cache forces a silent-auth iframe on every full
    // page reload, which the browser blocks on localhost (third-party cookies),
    // logging the tester out on refresh. Persist to localStorage + use refresh
    // tokens so reloads keep the session. Keyed off the actual runtime origin,
    // not VUE_APP_DASHBOARD_ENV: a CI-driven dev server runs on localhost with
    // its target backend's env name (e.g. 'test'), and needs the same
    // treatment. Deployed builds keep the original in-memory behavior.
    const isLocalTesting = isLocalDashboardHost(window.location.hostname)
    instance.auth0Client = markRaw(
      await createAuth0Client({
        ...pluginOptions,
        clientId: pluginOptions.clientId,
        domain: pluginOptions.domain,
        ...(isLocalTesting ? { cacheLocation: 'localstorage' } : {}),
        authorizationParams: {
          audience: pluginOptions.audience,
          scope: pluginOptions.scope,
          redirect_uri: redirectUri,
        },
      })
    )
    try {
      const search = window.location.search

      if ((search.includes('code=') || search.includes('error=')) && search.includes('state=')) {
        const { appState } = await instance.auth0Client.handleRedirectCallback()

        instance.appState = appState
      }
    } catch (error) {
      instance.error = error
    } finally {
      // The token/profile fetches below talk to Auth0; if any rejects (e.g.
      // getTokenSilently needing a blocked silent-auth iframe), we must still
      // resolve isLoading/readyPromise — otherwise the route guard waits forever
      // and the app hangs on a blank page. Surface the error so the Callback
      // view routes to /oops instead of stalling.
      let isAuthenticated = false
      try {
        isAuthenticated = await instance.auth0Client.isAuthenticated()

        if (isAuthenticated) {
          instance.idToken = await instance.auth0Client.getIdTokenClaims()
          instance.idTokenRaw = instance.idToken ? instance.idToken.__raw : ''
          // In previous implementation using auth0-js, we had manual method to check/set auth accessToken expiry and generate new one based on expiry status.
          // In auth0-spa-js, getTokenSilently returns new accessToken each time when called, it's all built in.
          // In this integration on each page load, accessToken will be a new one, we could remove all relevant legacy manual function.
          instance.accessToken = await instance.auth0Client.getTokenSilently()
          instance.user = await instance.auth0Client.getUser()
          instance.uid = instance.user[TenantConst.auth0ProfileKeys.uid]
          instance.tenants = instance.user[TenantConst.auth0ProfileKeys.tenants]
        }
      } catch (tokenError) {
        consoleError('Auth0 post-callback token/profile fetch failed', tokenError)
        instance.error = instance.error || tokenError
        isAuthenticated = false
      }

      instance.isAuthenticated = isAuthenticated
      instance.isLoading = false

      if (instance.readyResolve) {
        instance.readyResolve() // Resolve the promise when Auth0 is ready
      }
    }
  }

  init()

  return instance
}

/**
 *  Vue.js Plugin Definition
 */

export const Auth0Plugin = {
  install(app, options) {
    app.config.globalProperties.$auth0 = useAuth0(options)
  },
}
